import { useId, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReviewUnit } from "../../../api/content/curationApi";
import { askForReview, declineReviewRequest, withdrawReviewRequest, type ReviewRequest } from "../../../api/content/reviewCollaborationApi";
import { listItemReviewers } from "../../../api/access/accessApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import type { MemberNames } from "../../threads/data/useMemberNames";
import { orderedRequests, requestsForUnit, reviewableItemIds, type ItemReviewers } from "./requestScope";
import Chip from "../../../ui/feedback/Chip";
import type { ChipTone } from "../../../ui/styles/chipStyles";
import Field from "../../../ui/forms/Field";
import FieldGroup from "../../../ui/forms/FieldGroup";
import OptionLabel from "../../../ui/forms/OptionLabel";
import EmptyState from "../../../ui/feedback/EmptyState";

const STATE_TONE: Readonly<Record<ReviewRequest["state"], ChipTone>> = {
  open: "info",
  done: "success",
  declined: "warning",
  withdrawn: "neutral",
};

/**
 * "Ask someone to look" (curation-ui.md CU-10, hand-off): the requests about the unit in
 * focus, answering them (decline with a reason, withdraw), and asking a project member to
 * look at the unit or the whole change set. The form says who curates which of the unit's
 * items, since a curator can hand their part to a co-curator; a request grants nothing.
 * Hidden from someone who can't ask while there is none (P-B, W-09e); only the way to ask while there is none.
 */
export default function ReviewRequests({
  changeSetId,
  projectId,
  unit,
  requests,
  people,
  itemName,
  canAsk,
  onChanged,
}: {
  changeSetId: string;
  projectId: string;
  unit: ReviewUnit;
  requests: readonly ReviewRequest[];
  people: MemberNames;
  itemName: (itemId: string) => string;
  canAsk: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const [asking, setAsking] = useState(false);
  const shown = orderedRequests(requestsForUnit(requests, unit));
  if (shown.length === 0 && !canAsk) return null;
  const ask = canAsk && !asking ? (
    <AppButton
      size="xs"
      variant="ghost"
      onClick={() => {
        setAsking(true);
      }}
    >
      {t("reviews.collaboration.requests.ask")}
    </AppButton>
  ) : null;
  // Without requests, only the way to ask: no heading over an empty list (F145, usability round 4).
  if (shown.length === 0 && !asking) return <div>{ask}</div>;
  return (
    <section className="space-y-2" aria-labelledby={headingId}>
      {/* Wraps, so in a narrow pane (a Run's margin) the button goes under the heading instead of squeezing it. */}
      <div className="flex flex-wrap items-center gap-2">
        <h4 id={headingId} className="app-subsection-title min-w-[8rem] flex-1 text-sm">
          {t("reviews.collaboration.requests.title")}
        </h4>
        {ask}
      </div>
      {asking ? (
        <AskForm
          changeSetId={changeSetId}
          projectId={projectId}
          unit={unit}
          people={people}
          itemName={itemName}
          onDone={(sent) => {
            setAsking(false);
            if (sent) {
              onChanged();
            }
          }}
        />
      ) : null}
      <ul className="space-y-2">
        {shown.map((request) => (
          <RequestRow key={request.requestId} request={request} people={people} onChanged={onChanged} />
        ))}
      </ul>
    </section>
  );
}

function RequestRow({ request, people, onChanged }: { request: ReviewRequest; people: MemberNames; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState<string | null>(null);
  const answer = useSubmission();
  const mine = request.askedBy === people.currentUserId;
  const toMe = request.asked === people.currentUserId;
  const last = request.history.at(-1);
  const scope = request.unitKeys.length === 0 ? t("reviews.collaboration.requests.whole") : t("reviews.collaboration.requests.units", { count: request.unitKeys.length });
  return (
    <li className="app-card space-y-1 p-2 text-sm">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">{t("reviews.collaboration.requests.asked", { from: people.nameOf(request.askedBy), to: people.nameOf(request.asked) })}</span>
        <Chip tone={STATE_TONE[request.state]}>{t(`reviews.collaboration.requests.states.${request.state}`)}</Chip>
      </p>
      <p className="whitespace-pre-wrap">{request.note}</p>
      <p className="app-text-muted text-xs">
        {scope}, {formatTimeAgo(request.at, i18n.resolvedLanguage)}
        {last !== undefined && last.reason !== "" && request.state === "declined" ? `: ${last.reason}` : null}
      </p>
      {request.state === "open" && (toMe || mine) ? (
        <div className="flex flex-wrap items-center gap-2">
          {toMe && reason === null ? (
            <AppButton
              size="xs"
              variant="ghost"
              onClick={() => {
                setReason("");
              }}
            >
              {t("reviews.collaboration.requests.decline")}
            </AppButton>
          ) : null}
          {mine ? (
            <AppButton
              size="xs"
              variant="ghost"
              disabled={answer.state.status === "submitting"}
              onClick={() => {
                answer.submit(async () => {
                  await withdrawReviewRequest(request.requestId);
                  onChanged();
                });
              }}
            >
              {t("reviews.collaboration.requests.withdraw")}
            </AppButton>
          ) : null}
        </div>
      ) : null}
      {reason !== null ? (
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            answer.submit(async () => {
              await declineReviewRequest(request.requestId, reason.trim());
              setReason(null);
              onChanged();
            });
          }}
        >
          <input
            className={`${FORM_COMPACT_CONTROL_CLASS} min-w-0 flex-1`}
            aria-label={t("reviews.collaboration.requests.declineReason")}
            placeholder={t("reviews.collaboration.requests.declineReason")}
            value={reason}
            maxLength={2000}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          <AppButton size="xs" type="submit" disabled={reason.trim() === "" || answer.state.status === "submitting"}>
            {t("reviews.collaboration.requests.decline")}
          </AppButton>
        </form>
      ) : null}
      {answer.state.status === "failed" ? <ErrorNotice error={answer.state.error} compact={true} /> : null}
    </li>
  );
}

function AskForm({
  changeSetId,
  projectId,
  unit,
  people,
  itemName,
  onDone,
}: {
  changeSetId: string;
  projectId: string;
  unit: ReviewUnit;
  people: MemberNames;
  itemName: (itemId: string) => string;
  onDone: (sent: boolean) => void;
}) {
  const { t } = useTranslation();
  // Who may review each of the unit's items, as the server's permission check answers it (one call per item).
  const loadReviewers = useCallback(
    async (signal: AbortSignal): Promise<ItemReviewers> =>
      new Map(await Promise.all(unit.items.map(async (item) => [item.itemId, (await listItemReviewers(projectId, item, signal)).map((person) => person.userId)] as const))),
    [projectId, unit.items],
  );
  const reviewers = useLoad(loadReviewers);
  const candidates = people.members.filter((member) => member.userId !== people.currentUserId);
  const [asked, setAsked] = useState("");
  const [wholeChangeSet, setWholeChangeSet] = useState(unit.kind === "conversation");
  const [note, setNote] = useState("");
  const sending = useSubmission();
  const curated = (userId: string) =>
    reviewers.state.status === "loaded" ? reviewableItemIds(reviewers.state.value, userId, unit.items).map(itemName) : [];
  return (
    <form
      className="app-card space-y-2 p-2 text-sm"
      aria-label={t("reviews.collaboration.requests.ask")}
      onSubmit={(event) => {
        event.preventDefault();
        sending.submit(async () => {
          await askForReview(changeSetId, { asked, unitKeys: wholeChangeSet ? [] : [unit.key], note: note.trim() });
          onDone(true);
        });
      }}
    >
      <Field label={t("reviews.collaboration.requests.whom")}>
        {(control) => (
          <select
            {...control}
            className={FORM_SELECT_CLASS}
            value={asked}
            onChange={(event) => {
              setAsked(event.target.value);
            }}
          >
            <option value="">{t("reviews.collaboration.requests.choose")}</option>
            {candidates.map((member) => {
              const items = curated(member.userId);
              return (
                <option key={member.userId} value={member.userId}>
                  {items.length > 0 ? t("reviews.collaboration.requests.curates", { name: member.displayName, items: items.join(", ") }) : member.displayName}
                </option>
              );
            })}
          </select>
        )}
      </Field>
      {unit.kind === "conversation" ? null : (
        <FieldGroup legend={t("reviews.collaboration.requests.scope")} legendHidden className="flex flex-wrap gap-3">
          <OptionLabel>
            <input
              type="radio"
              name="request-scope"
              checked={!wholeChangeSet}
              onChange={() => {
                setWholeChangeSet(false);
              }}
            />
            {t("reviews.collaboration.requests.thisUnit")}
          </OptionLabel>
          <OptionLabel>
            <input
              type="radio"
              name="request-scope"
              checked={wholeChangeSet}
              onChange={() => {
                setWholeChangeSet(true);
              }}
            />
            {t("reviews.collaboration.requests.whole")}
          </OptionLabel>
        </FieldGroup>
      )}
      <Field label={t("reviews.collaboration.requests.note")}>
        {(control) => (
          <textarea
            {...control}
            className={FORM_COMPACT_CONTROL_CLASS}
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        )}
      </Field>
      {candidates.length === 0 ? <EmptyState size="small">{t("reviews.collaboration.requests.nobody")}</EmptyState> : null}
      <div className="flex gap-2">
        <AppButton size="xs" type="submit" disabled={asked === "" || note.trim() === "" || sending.state.status === "submitting"}>
          {t("reviews.collaboration.requests.send")}
        </AppButton>
        <AppButton
          size="xs"
          variant="ghost"
          onClick={() => {
            onDone(false);
          }}
        >
          {t("common.cancel")}
        </AppButton>
      </div>
      {sending.state.status === "failed" ? <ErrorNotice error={sending.state.error} compact={true} /> : null}
    </form>
  );
}
